/**
 * Registro attività della societa (V53) e della piattaforma (V57): le azioni
 * che vi si scrivono, i tipi con cui si filtrano e i filtri delle due viste.
 *
 * La specifica chiede cancellazioni, modifiche ai ruoli e azioni
 * dell'assistente. Il tipo raggruppa le azioni in quelle categorie, piu le
 * azioni del Super Admin sulla societa (creazione, stato, limiti). Le azioni
 * dell'assistente arriveranno con le sue scritture, e con loro un altro tipo:
 * finche legge soltanto non ha nulla da registrare.
 */

import { z } from 'zod';

export const AZIONI_REGISTRO = [
  'ruolo.assegnato',
  'ruolo.revocato',
  'account.disattivato',
  'account.riattivato',
  'account.invitato',
  'invito.reinviato',
  'invito.annullato',
  'invito.accettato',
  'allenamento.eliminato',
  'blocco.eliminato',
  'partita.carico_sostituito',
  'attributo.eliminato',
  'misurazione.eliminata',
  'test.eliminato',
  'esecuzione.eliminata',
  'competizione.eliminata',
  'admin.nominato',
  'societa.creata',
  'societa.disattivata',
  'societa.riattivata',
  'societa.limiti_modificati',
] as const;

export type AzioneRegistro = (typeof AZIONI_REGISTRO)[number];

export const TIPI_REGISTRO = ['ruoli', 'cancellazioni', 'piattaforma'] as const;
export type TipoRegistro = (typeof TIPI_REGISTRO)[number];

export const ETICHETTA_TIPO_REGISTRO: Record<TipoRegistro, string> = {
  ruoli: 'Ruoli e accessi',
  cancellazioni: 'Cancellazioni',
  piattaforma: 'Piattaforma',
};

export const TIPO_AZIONE: Record<AzioneRegistro, TipoRegistro> = {
  'ruolo.assegnato': 'ruoli',
  'ruolo.revocato': 'ruoli',
  'account.disattivato': 'ruoli',
  'account.riattivato': 'ruoli',
  'account.invitato': 'ruoli',
  'invito.reinviato': 'ruoli',
  'invito.annullato': 'ruoli',
  'invito.accettato': 'ruoli',
  'allenamento.eliminato': 'cancellazioni',
  'blocco.eliminato': 'cancellazioni',
  'partita.carico_sostituito': 'cancellazioni',
  'attributo.eliminato': 'cancellazioni',
  'misurazione.eliminata': 'cancellazioni',
  'test.eliminato': 'cancellazioni',
  'esecuzione.eliminata': 'cancellazioni',
  'competizione.eliminata': 'cancellazioni',
  'admin.nominato': 'ruoli',
  'societa.creata': 'piattaforma',
  'societa.disattivata': 'piattaforma',
  'societa.riattivata': 'piattaforma',
  'societa.limiti_modificati': 'piattaforma',
};

export const azioniDelTipo = (tipo: TipoRegistro): AzioneRegistro[] =>
  AZIONI_REGISTRO.filter((a) => TIPO_AZIONE[a] === tipo);

/** Un'azione scritta prima che il registro la conoscesse non ha tipo. */
export const tipoDellAzione = (azione: string): TipoRegistro | null =>
  (TIPO_AZIONE as Record<string, TipoRegistro | undefined>)[azione] ?? null;

/** Righe per pagina: le altre si caricano dal fondo della vista. */
export const RIGHE_REGISTRO = 50;

const vuotoComeAssente = <T extends z.ZodTypeAny>(schema: T) =>
  z
    .union([z.literal(''), schema])
    .optional()
    .transform((v) => (v === '' ? undefined : (v as z.infer<T> | undefined)));

const campiFiltri = {
  autore: vuotoComeAssente(z.string().uuid('Autore non valido.')),
  tipo: vuotoComeAssente(z.enum(TIPI_REGISTRO)),
  da: vuotoComeAssente(z.string().date('Data non valida.')),
  a: vuotoComeAssente(z.string().date('Data non valida.')),
  dopo: vuotoComeAssente(z.string().regex(/^[^~]+~\d+$/, 'Cursore non valido.')),
};

/** Toglie le chiavi assenti, perche il tipo di uscita non le porti come undefined. */
const pulisci = <T extends Record<string, unknown>>(f: T) => {
  const puliti: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined) puliti[k] = v;
  }
  return puliti as { [K in keyof T]?: Exclude<T[K], undefined> };
};

/**
 * Filtri dalla query string. `dopo` e il cursore della pagina successiva,
 * come lo restituisce il server: si passa indietro senza interpretarlo.
 */
export const filtriRegistroSchema = z.object(campiFiltri).transform(pulisci);

export type FiltriRegistro = z.output<typeof filtriRegistroSchema>;

/** Le righe senza societa: scritte per una societa poi cancellata. */
export const SENZA_SOCIETA = 'nessuna';

/** Il registro di piattaforma (V57) filtra anche per societa. */
export const filtriRegistroPiattaformaSchema = z
  .object({
    ...campiFiltri,
    societa: vuotoComeAssente(
      z.union([z.literal(SENZA_SOCIETA), z.string().uuid('Società non valida.')]),
    ),
  })
  .transform(pulisci);

export type FiltriRegistroPiattaforma = z.output<typeof filtriRegistroPiattaformaSchema>;
